import React from "react"
import { RequestError } from "../shared/RequestError"
import { FeedSpinner } from "../shared/Spinner"
import { PostListItem } from "../posts/PostListItem"
import PostListItemShort from "../posts/PostListItemShort"
import { ErrorBoundary } from "../shared/ErrorBoundary"
import { useInfiniteBandPosts } from "../../hooks/useBandPosts"
import type { AlbumDisplayConfig } from "../../types/albumDisplay"
import type { HeadlineDisplayConfig } from "../../types/headlineDisplay"
import type { VideoDisplayConfig } from "../../types/videoDisplay"

interface PostSectionProps {
  title?: string
  bandId: number
  bandName: string
  contentType: string
  colClass: string
  adminMode?: boolean
  pageSize?: number
  variant?: "short"
  noResultsMessage?: string
  albumConfig?: AlbumDisplayConfig
  headlineConfig?: HeadlineDisplayConfig
  videoConfig?: VideoDisplayConfig
}

export const PostSection = ({
  title,
  bandId,
  bandName,
  contentType,
  colClass,
  adminMode = false,
  pageSize = 4,
  variant,
  noResultsMessage,
  albumConfig,
  headlineConfig,
  videoConfig,
}: PostSectionProps) => {
  const effectivePageSize = adminMode ? 12 : pageSize
  const {
    data,
    error,
    fetchNextPage,
    isFetching,
    status,
  } = useInfiniteBandPosts({
    bandId,
    contentType,
    adminMode,
    pageSize: effectivePageSize,
  })
  const lastPage = status === "success" ? data.pages.slice(-1)[0] : null

  return (
    <ErrorBoundary>
      <div id={contentType}>
        {status === "loading" ? (
          <FeedSpinner />
        ) : status === "error" ? (
          <RequestError error={error} />
        ) : (
          <>
            {data.pages[0].total_count === 0 && noResultsMessage && (
              <p className="text-gray-500 text-sm py-4">{noResultsMessage}</p>
            )}
            {data.pages[0].total_count > 0 && (
              <>
                {title && (
                  <h2 className="border-b-4 border-black font-bold my-4 text-2xl uppercase">
                    {title}
                  </h2>
                )}
                <div>
                  <ul className={`grid ${colClass} gap-8`}>
                    {data.pages.map((page, i) => (
                      <React.Fragment key={page.page}>
                        {page.posts.map(post => (
                          variant === "short" ? (
                            <PostListItemShort
                              key={`${post.id}:${adminMode}`}
                              post={post}
                              adminMode={adminMode}
                              albumConfig={albumConfig}
                              headlineConfig={headlineConfig}
                              videoConfig={videoConfig}
                            />
                          ) : (
                            <PostListItem
                              key={`${post.id}:${adminMode}`}
                              post={post}
                              adminMode={adminMode}
                            />
                          )
                        ))}
                      </React.Fragment>
                    ))}
                  </ul>
                  {lastPage.has_next_page && (
                    <button
                      className="text-blue-600 focus:outline-none"
                      onClick={() => fetchNextPage()}
                      disabled={isFetching}
                    >
                      {isFetching
                        ? "loading..."
                        : `See more ${(title ?? "posts").toLowerCase()} from ${bandName}`}
                    </button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </ErrorBoundary>
  )
}
